import { MutableRefObject, useCallback, useEffect, useState } from 'react';
import { SimpleMemoryCache } from '../../common/caches/simple-memory-cache';
import { FeatureLayer, Graphic, WebMap } from '../constructors';
import { createCirclesFeatureLayer } from '../helpers/feature-layers';
import { createCircleGraphic } from '../helpers/graphics';
import type { CircleGeoPointEntity } from '../types';
import { useLayerControl, usePreviousLayer } from './use-feature-layer-control';

/**
 * This hook renders an array of circles in a FeatureLayer.
 */
type CircleFeatureLayerProps = {
    webMap?: WebMap;
    layerKey: string; // must start with '_', otherwise map "Layers" dropdown will allow user to toggle this layer.
    layerId: number; // unique id. no duplicate id from FEATURE_LAYERS_PROPERTIES
    title: string;
    color: number[];
    circles: CircleGeoPointEntity[];
    shouldShowRadius?: boolean;
    isOnGraphic?: (graphic: Graphic) => boolean;
    entityIdToObjectIdMapCacheRef: MutableRefObject<SimpleMemoryCache<number>>;
};
export const useCircleFeatureLayer = ({
    webMap,
    layerKey,
    layerId,
    title,
    circles,
    color,
    shouldShowRadius,
    entityIdToObjectIdMapCacheRef,
}: CircleFeatureLayerProps) => {
    const [circlesFeatureLayer, setCirclesFeatureLayer] = useState<FeatureLayer | undefined>();
    const prevShouldShowRadius = usePreviousLayer(shouldShowRadius, circlesFeatureLayer);

    // didItemChange callback for useLayerControl hook.
    // The item changes if
    //   - radius changes or
    //   - shouldShowRadius flag changes or
    const didItemChange = useCallback(
        (curr: CircleGeoPointEntity, prev: CircleGeoPointEntity) => {
            return curr.radius !== prev.radius || shouldShowRadius !== prevShouldShowRadius;
        },
        [prevShouldShowRadius, shouldShowRadius]
    );

    // createCircleGraphics callback for useLayerControl hook.
    // create the circle items
    const createCircleGraphics = useCallback(
        (circles: CircleGeoPointEntity[]) => {
            const circleGraphics: Graphic[] = [];
            if (entityIdToObjectIdMapCacheRef?.current) {
                for (const circle of circles) {
                    const { entityId, latitude, longitude, radius } = circle;
                    const objectId = entityIdToObjectIdMapCacheRef.current.get(entityId);
                    const circleGraphic = createCircleGraphic({
                        center: { latitude, longitude },
                        radius,
                        entityId,
                        objectId,
                        shouldShowRadius,
                    });
                    if (circleGraphic) {
                        circleGraphics.push(circleGraphic);
                    }
                }
            }
            return circleGraphics;
        },
        [shouldShowRadius, entityIdToObjectIdMapCacheRef]
    );

    // handles initializing empty feature layer
    useEffect(() => {
        // need modules to load
        if (webMap) {
            const featureLayer = createCirclesFeatureLayer({
                layerKey,
                layerId,
                title,
                color,
            });
            setCirclesFeatureLayer(featureLayer);
            webMap.layers.add(featureLayer);
        }
        // including color results in an infinte loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [webMap, title]);

    useLayerControl<CircleGeoPointEntity>({
        layer: circlesFeatureLayer,
        currItems: circles,
        didItemChange,
        createGraphics: createCircleGraphics,
        entityIdToObjectIdMapCacheRef,
    });

    return { circlesFeatureLayer };
};
